import React, { useMemo } from 'react'
import Group from 'components/utils/Group'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import type { OfferPricingData } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useMaldivesOffersPricing'
import { pluralizeToString } from 'lib/string/pluralize'

interface Props {
  offer: App.Offer | App.OfferSummary
  pricing: OfferPricingData
  isPricePerPerson?: boolean
  discountPercentage?: number
  nights?: number
}

function CharterBundlePricing({ offer: _offer, pricing, isPricePerPerson, discountPercentage, nights }: Props) {
  const displayPrice = isPricePerPerson
    ? pricing.bundlePricePerPerson
    : pricing.bundleTotalPrice
  const memberDisplayPrice = isPricePerPerson
    ? pricing.bundleMemberPricePerPerson
    : pricing.bundleMemberTotalPrice
  const valuePrice = pricing.hotelValue && pricing.flightTotalPrice
    ? (pricing.hotelValue + pricing.flightTotalPrice)
    : undefined

  // Always show LP+ pricing block if member pricing exists and is lower than regular price
  // This displays both prices to all users (logged in or out) to promote LP+ membership
  const showMemberPricing = memberDisplayPrice && memberDisplayPrice < (displayPrice ?? 0)

  const priceElement = useMemo(() => {
    if (showMemberPricing) {
      return (
        <LuxPlusPriceStack
          price={displayPrice ?? 0}
          memberPrice={memberDisplayPrice}
          saleUnit={isPricePerPerson ? 'person' : 'total'}
          size="L"
        />
      )
    }
    return (
      <PriceRowPrice
        size="L"
        price={displayPrice ?? 0}
        saleUnit={isPricePerPerson ? 'person' : undefined}
      />
    )
  }, [showMemberPricing, displayPrice, memberDisplayPrice, isPricePerPerson])

  const roomCount = pricing.roomBreakdown.length || 1
  const guestCount = pricing.totalGuests || 2
  const roomCaption = isPricePerPerson
    ? `${guestCount} ${guestCount === 1 ? 'person' : 'people'}`
    : `${roomCount} room${roomCount > 1 ? 's' : ''}`

  const resolvedNights = nights ?? pricing.allocatedPackages[0]?.package?.duration

  const priceCaption = resolvedNights
    ? `${pluralizeToString('night', resolvedNights)} & flights from`
    : isPricePerPerson ? 'From' : 'Total from'

  return (
    <Group direction="vertical" gap={4} fullWidth>
      <PriceRowPriceCaption>
        {priceCaption}
      </PriceRowPriceCaption>
      {priceElement}
      {valuePrice && discountPercentage && discountPercentage > 0 && (
        <PriceRowValueDiscountWithCaption
          size="M"
          originalValue={valuePrice}
          discountPercentage={discountPercentage}
        />
      )}
      <PriceRowCaption>{roomCaption}</PriceRowCaption>
      <PriceRowTaxesAndFees />
    </Group>
  )
}

export default CharterBundlePricing
